import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { completeLocalRecovery, startLocalRecovery } from "@/api/auth";
import { ApiError } from "@/api/client";
import { AuthShell } from "@/components/layout/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PASSWORD_LENGTH_ERROR_MESSAGE,
  PASSWORD_LENGTH_HINT,
  isPasswordLengthValid,
} from "./password-policy";

export function LocalRecoveryRequestPage() {
  const [email, setEmail] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setRequestError("");
    try {
      await startLocalRecovery(email.trim().toLowerCase());
      setAccepted(true);
    } catch (error) {
      setRequestError(error instanceof ApiError && error.status === 429
        ? "操作回数の上限に達しました。時間をおいて再度お試しください。"
        : error instanceof ApiError && error.status === 400
          ? "入力内容を確認して、再度お試しください。"
          : "再設定手順の受付を確認できませんでした。時間をおいて再度お試しください。");
    } finally { setBusy(false); }
  };

  return <AuthShell title="ローカル認証を復旧" description="ローカル認証の有無や登録状況を区別しない共通の受付画面です。">
    {accepted ? <div className="space-y-4"><p role="status" className="rounded-md border bg-muted p-4 text-sm">入力された宛先が再設定対象の場合は、手順を送信しました。</p><Link className="text-primary underline" to="/login">ログインへ戻る</Link></div> :
      <form className="space-y-4" onSubmit={submit}>{requestError && <p role="alert">{requestError}</p>}<div className="space-y-2"><Label htmlFor="reset-email">メールアドレス</Label><Input id="reset-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={busy} required /></div><Button className="w-full" disabled={busy}>再設定手順を送信</Button></form>}
  </AuthShell>;
}

export function LocalRecoveryPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(() =>
    new URLSearchParams(globalThis.location.hash.slice(1)).get("token")
  );
  useEffect(() => {
    const captureFragmentToken = () => {
      const presented = new URLSearchParams(globalThis.location.hash.slice(1)).get("token");
      if (presented) setToken(presented);
      globalThis.history.replaceState(null, "", globalThis.location.pathname);
    };
    captureFragmentToken();
    globalThis.addEventListener("hashchange", captureFragmentToken);
    return () => globalThis.removeEventListener("hashchange", captureFragmentToken);
  }, []);
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [error, setError] = useState("");
  const [terminalFailure, setTerminalFailure] = useState<"reissue" | "result_unknown" | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!token) {
      setError("再設定リンクを利用できません。メールのリンクからもう一度開いてください。");
      return;
    }
    if (!isPasswordLengthValid(password)) {
      setError(PASSWORD_LENGTH_ERROR_MESSAGE);
      return;
    }
    if (password !== passwordConfirmation) {
      setError("確認用パスワードが一致しません。");
      return;
    }
    setBusy(true);
    setError("");
    const currentToken = token;
    try {
      await completeLocalRecovery(currentToken, password);
      setToken(null);
      setPassword("");
      setPasswordConfirmation("");
      navigate("/login", { replace: true });
    } catch (cause) {
      setPassword("");
      setPasswordConfirmation("");
      if (cause instanceof ApiError && cause.status === 422 && cause.code === "password_policy_violation") {
        setError("このパスワードは使用できません。別のパスワードを入力してください。");
      } else if (cause instanceof ApiError &&
          ((cause.status === 401 && cause.code === "authentication_failed") ||
            (cause.status === 409 && cause.code === "auth_method_conflict"))) {
        setToken(null);
        setTerminalFailure("reissue");
      } else {
        // A transport failure can occur after the credential transaction has
        // committed. Treat every unclassified completion failure as
        // result-unknown and never invite a blind replay from this screen.
        setToken(null);
        setTerminalFailure("result_unknown");
      }
    } finally { setBusy(false); }
  };

  if (terminalFailure === "result_unknown") {
    return <AuthShell title="変更結果を確認できません" description="重複処理を避けるため、この画面から再送しません。"><Link className="text-primary underline" to="/login">新しいパスワードでログインを試す</Link></AuthShell>;
  }
  if (terminalFailure === "reissue") {
    return <AuthShell title="再設定リンクを利用できません" description="リンクは使用済み、期限切れ、または現在のアカウント状態と一致しません。"><Link className="text-primary underline" to="/auth/local-recovery/request">新しいリンクを発行する</Link></AuthShell>;
  }

  return <AuthShell title="ローカル認証のパスワードを設定" description="リンクは一度だけ使用できます。外部ログインだけのアカウントではローカル認証を追加します。">
    {error ? <div role="alert" className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div> : null}
    <form className="space-y-4" onSubmit={submit}><div className="space-y-2"><Label htmlFor="reset-password">新しいパスワード</Label><Input id="reset-password" type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} /><p className="text-xs text-muted-foreground">{PASSWORD_LENGTH_HINT}</p></div><div className="space-y-2"><Label htmlFor="reset-password-confirmation">新しいパスワード（確認）</Label><Input id="reset-password-confirmation" type="password" autoComplete="new-password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} disabled={busy} /></div><Button className="w-full" disabled={busy}>パスワードを変更</Button></form>
  </AuthShell>;
}
